'use client';

import React, { useState } from 'react';
import { Send, CheckCircle, AlertCircle } from 'lucide-react';

interface ErrorFeedbackProps {
  error: Error;
  componentStack?: string;
  url: string;
}

export default function ErrorFeedback({ error, componentStack, url }: ErrorFeedbackProps) {
  const [feedback, setFeedback] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error2, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!feedback.trim()) return;

    setSubmitting(true);
    setError(null);

    try {
      const response = await fetch('/api/client-error/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          errorMessage: error.message,
          errorStack: error.stack,
          componentStack,
          url,
          userFeedback: feedback,
          timestamp: new Date().toISOString()
        })
      });

      if (response.ok) {
        setSubmitted(true);
      } else {
        setError('Failed to submit feedback. Please try again.');
      }
    } catch (_e) {
      setError('Failed to submit feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="bg-positive-50 border-2 border-positive-200 rounded-lg p-6 text-center">
        <CheckCircle className="w-12 h-12 text-positive-600 mx-auto mb-3" />
        <h3 className="text-lg font-semibold text-positive-800 mb-2">Thank you!</h3>
        <p className="text-positive-700 text-sm">
          Your feedback helps us fix this issue faster.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 border-2 border-black rounded-lg p-6">
      <h3 className="text-lg font-bold mb-3 flex items-center gap-2">
        <AlertCircle className="w-5 h-5 text-positive-600" />
        Help us fix this
      </h3>
      <p className="text-sm text-gray-600 mb-4">
        What were you doing when this happened? Any details help us reproduce and fix the issue.
      </p>
      
      <form onSubmit={handleSubmit}>
        <textarea
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          placeholder="e.g., I was on the dashboard trying to add a new asset..."
          className="w-full p-3 border-2 border-black rounded-lg font-mono text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-positive-500"
          rows={4}
          disabled={submitting}
        />
        
        {error2 && (
          <p className="text-danger-600 text-sm mb-3">{error2}</p>
        )}
        
        <button
          type="submit"
          disabled={submitting || !feedback.trim()}
          className="bg-positive-600 hover:bg-positive-700 disabled:bg-gray-400 text-white font-bold py-2 px-6 rounded-md border-2 border-ink transition-all flex items-center gap-2"
        >
          {submitting ? (
            <>Sending...</>
          ) : (
            <>
              <Send className="w-4 h-4" />
              Send Feedback
            </>
          )}
        </button>
      </form>
    </div>
  );
}
